// CommentsPage.jsx
// Imports:
//   ../../styles/operaCloud.css  — shared Opera Cloud design tokens
//   ./CommentsPage.css           — page-specific layout & modals

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../styles/operaCloud.css";
import "./CommentsPage.css";
import { getMemberComments, postMemberComment, putMemberComment, getLocations, getLocation } from '../../api/operaExpApi';
import useAuthSafe from '../../auth/useAuthSafe';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const PRIORITIES    = ["1-Highest", "2-High", "3-Medium", "4-Lower", "5-Lowest"];
const SOURCES       = ["All", "HALO", "CMP", "Opera"];
const COMMENT_TYPES = ["Customer Concerns", "Customer Request", "User Comment"];
const DEPARTMENTS   = ["All", "Corp IT"];

// Fallback property code — used when no property is selected / API list not yet loaded.
const PROPERTY_CODE = "940";

const EMPTY_FORM = {
  priority:            "",
  limitDepartment:     "All",
  limitProperty:       [],  // array of selected property codes
  globalAllProperties: true,  // default: Global (All Properties) checked on Add/Reply
  sourceProperty:      PROPERTY_CODE, // first selected code; fallback to hardcoded 940
  commentType:         "User Comment", // default comment type on Add/Reply
  expirationDate:      "",
  never:               false,
  commentText:         "",
};

// Panel modes
// "idle"  → dimmed bottom panel, no row selected
// "view"  → row selected, read-only + Reply/Edit/Delete/Settle buttons
// "add"   → new comment form
// "edit"  → edit existing comment form
// "reply" → reply to existing comment form
const MODE = { IDLE: "idle", VIEW: "view", ADD: "add", EDIT: "edit", REPLY: "reply", VIEW_REPLY: "view_reply", EDIT_REPLY: "edit_reply" };

// ─────────────────────────────────────────────────────────────
// HOOK — detect popup iframe size (≤600 px)
// ─────────────────────────────────────────────────────────────
function usePopupMode() {
  const [isPopup, setIsPopup] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsPopup(window.innerWidth <= 600 || window.innerHeight <= 600);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isPopup;
}

// ─────────────────────────────────────────────────────────────
// HOOK — toast notifications
// ─────────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);

  const show = useCallback((message, duration = 20000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, exiting: false }]);

    // Start exit animation 300ms before removal
    setTimeout(() => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
      );
    }, duration - 300);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);

    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  return { toasts, show, dismiss };
}

// ─────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────

// ── Multi-select dropdown for "Limit to Property" ────────────
// value    = string[]  (selected locationCode values)
// onChange = (string[]) => void
// Displays locationLegalName in the list; stores locationCode as the selected value.
// propertyList = [{ locationLegalName, locationCode, pmsHotelId }]
function PropertyMultiSelect({ value, onChange, disabled, placeholder, propertyList = [] }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef             = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const selected = Array.isArray(value) ? value : [];

  // Filter by legal name text
  const filtered = propertyList.filter((p) =>
    p.locationLegalName.toLowerCase().includes(search.toLowerCase())
  );

  function nameFromCode(code) {
    return propertyList.find((p) => p.locationCode === code)?.locationLegalName ?? code;
  }

  function toggle(code) {
    if (selected.includes(code)) onChange(selected.filter((v) => v !== code));
    else onChange([...selected, code]);
  }

  const defaultPlaceholder = placeholder || "Select...";
  const selectedNames = selected.map(nameFromCode);
  const displayText =
    selected.length === 0   ? defaultPlaceholder
    : selected.length <= 2  ? selectedNames.join(", ")
    : `${selected.length} selected`;

  return (
    <div className="cp-ms-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`cp-ms-trigger${disabled ? " cp-ms-disabled" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={selected.length ? selectedNames.join(", ") : "Select properties"}
      >
        <span className="cp-ms-display">{displayText}</span>
        <span className="cp-ms-caret" />
      </button>

      {open && !disabled && (
        <div className="cp-ms-dropdown">
          <div className="cp-ms-search-wrap">
            <input
              className="oc-input cp-ms-search"
              placeholder="Search properties..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="cp-ms-list">
            {filtered.length === 0 ? (
              <div className="cp-ms-empty">
                {propertyList.length === 0 ? 'Loading properties...' : 'No results'}
              </div>
            ) : (
              filtered.map((prop) => (
                <label
                  key={prop.locationCode}
                  className={`cp-ms-item${selected.includes(prop.locationCode) ? " cp-ms-item-sel" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(prop.locationCode)}
                    onChange={() => toggle(prop.locationCode)}
                  />
                  <span>{prop.locationLegalName}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className, required }) {
  return (
    <div className={["cp-field", className].filter(Boolean).join(" ")}>
      <label className={required ? "oc-label required" : "oc-label"}>
        {label}
      </label>
      {children}
    </div>
  );
}

function DateField({ label, value, onChange, disabled, required }) {
  const ref = useRef(null);
  function openPicker() {
    if (!ref.current) return;
    if (typeof ref.current.showPicker === "function") ref.current.showPicker();
    else ref.current.focus();
  }
  return (
    <Field label={label} required={required}>
      <div className="oc-date-wrap">
        <input
          ref={ref}
          type="date"
          className="oc-date-input"
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
        <button
          type="button"
          className="oc-cal-btn"
          onClick={openPicker}
          disabled={disabled}
          title="Open calendar"
          aria-label="Open calendar"
        />
      </div>
    </Field>
  );
}

// Toast container (rendered at root level)
function ToastContainer({ toasts, dismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="cp-toast-container">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`cp-toast${t.exiting ? " cp-toast-exit" : ""}`}
        >
          <span className="cp-toast-icon">✓</span>
          <span className="cp-toast-msg">{t.message}</span>
          <button className="cp-toast-close" onClick={() => dismiss(t.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}

// Simple MESSAGE popup — used after successful add/edit
function SuccessMessageModal({ message, onOk }) {
  return (
    <div className="cp-overlay">
      <div className="cp-modal cp-msg-modal">
        <div className="cp-modal-header">
          <span>Message</span>
          <button className="cp-modal-close-btn" onClick={onOk}>CLOSE ✕</button>
        </div>
        <div className="cp-modal-body cp-msg-body">
          <span className="cp-msg-text">{message}</span>
        </div>
        <div className="cp-modal-footer">
          <button className="oc-btn" onClick={onOk}>Ok</button>
        </div>
      </div>
    </div>
  );
}

// ── Settle/Void reasons (matches Authorization screen options)
const SV_REASONS = ["Comment Not Needed", "System Testing", "User Error"];

// Three-step Settle/Void flow:
//   Step 1 — choose Settle or Void + optional note
//   Step 2 — Authorization: pick a reason + optional comment, call API
//   Step 3 — Success message (or inline error stays on step 2)
function SettleVoidFlow({ onDone, onClose }) {
  const [step,         setStep]        = useState(1);
  const [action,       setAction]      = useState("Settle");  // "Settle" | "Void"
  const [step1Comment, setStep1Comment]= useState("");
  const [reason,       setReason]      = useState("");
  const [authComment,  setAuthComment] = useState("");
  const [error,        setError]       = useState(null);

  // ── Step 2 submit — no backend call; functionality not yet available
  function handleAuthSubmit() {
    if (!reason) { setError('Please select a reason.'); return; }
    setError(null);
    setStep(3);
  }

  // ─── STEP 1 — Settle or Void selector
  if (step === 1) {
    return (
      <div className="cp-overlay">
        <div className="cp-modal">
          <div className="cp-modal-header">
            <span>Settle / Void Comment</span>
            <button className="cp-modal-close-btn" onClick={onClose}>CLOSE ✕</button>
          </div>
          <div className="cp-modal-body">
            <div className="cp-sv-radios">
              {['Settle', 'Void'].map((opt) => (
                <label key={opt} className="cp-sv-radio-label">
                  <input
                    type="radio" name="sv-action" value={opt}
                    checked={action === opt}
                    onChange={() => setAction(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
            <div className="cp-sv-comment-label">Comment</div>
            <textarea
              className="cp-sv-textarea"
              value={step1Comment}
              onChange={(e) => setStep1Comment(e.target.value)}
              placeholder="Optional comment..."
            />
          </div>
          <div className="cp-modal-footer">
            <button className="oc-btn" onClick={() => setStep(2)}>Submit</button>
            <button className="oc-btn oc-btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2 — Authorization (reason + comment, no password)
  if (step === 2) {
    return (
      <div className="cp-overlay">
        <div className="cp-modal cp-auth-modal">
          <div className="cp-modal-header">
            <span>Authorization</span>
            <button className="cp-modal-close-btn" onClick={onClose}>CLOSE ✕</button>
          </div>
          <div className="cp-modal-body">

            {/* Reason */}
            <div className="cp-auth-row">
              <label className="cp-auth-label">Reason:</label>
              <div className="cp-auth-field">
                <select
                  className="oc-select cp-auth-select"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setError(null); }}
                  >
                  <option value="">Select Reason</option>
                  {SV_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {!reason && <span className="cp-auth-required">Required</span>}
              </div>
            </div>

            {/* Comments */}
            <div className="cp-auth-row cp-auth-row-textarea">
              <label className="cp-auth-label">Comments:</label>
              <div className="cp-auth-field">
                <textarea
                  className="cp-sv-textarea"
                  value={authComment}
                  onChange={(e) => setAuthComment(e.target.value)}
                  placeholder="Optional comments..."
                />
              </div>
            </div>

            {/* Inline error */}
            {error && <div className="cp-auth-error">{error}</div>}

          </div>
          <div className="cp-modal-footer">
            <button className="oc-btn" onClick={handleAuthSubmit}>
              Submit
            </button>
            <button className="oc-btn oc-btn-secondary" onClick={() => { setStep(1); setError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 3 — Not supported message (shown for both Settle and Void)
  return (
    <div className="cp-overlay">
      <div className="cp-modal cp-msg-modal">
        <div className="cp-modal-header">
          <span>Message</span>
          <button className="cp-modal-close-btn" onClick={onDone}>CLOSE ✕</button>
        </div>
        <div className="cp-modal-body cp-msg-body">
          <span className="cp-msg-text" style={{ color: 'var(--oc-error, #c0392b)' }}>
            This functionality is not supported at this point.
          </span>
        </div>
        <div className="cp-modal-footer">
          <button className="oc-btn" onClick={onDone}>Ok</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DELETE FLOW — Confirm → Authorization → Not-supported message
// ─────────────────────────────────────────────────────────────
function DeleteFlow({ onClose }) {
  const [step,        setStep]      = useState(1);
  const [reason,      setReason]    = useState('');
  const [authComment, setAuthComment] = useState('');
  const [error,       setError]     = useState(null);

  if (step === 1) {
    return (
      <div className="cp-overlay">
        <div className="cp-modal cp-msg-modal">
          <div className="cp-modal-header">
            <span>Confirmation</span>
            <button className="cp-modal-close-btn" onClick={onClose}>CLOSE ✕</button>
          </div>
          <div className="cp-modal-body cp-msg-body">
            <span className="cp-msg-text" style={{ color: 'var(--oc-text, #1a1a1a)' }}>
              Are you sure you want to Delete the Comment?
            </span>
          </div>
          <div className="cp-modal-footer">
            <button className="oc-btn" onClick={() => setStep(2)}>Confirm</button>
            <button className="oc-btn oc-btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="cp-overlay">
        <div className="cp-modal cp-auth-modal">
          <div className="cp-modal-header">
            <span>Authorization</span>
            <button className="cp-modal-close-btn" onClick={onClose}>CLOSE ✕</button>
          </div>
          <div className="cp-modal-body">
            <div className="cp-auth-row">
              <label className="cp-auth-label">Reason:</label>
              <div className="cp-auth-field">
                <select
                  className="oc-select cp-auth-select"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setError(null); }}
                >
                  <option value="">Select Reason</option>
                  {SV_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {!reason && <span className="cp-auth-required">Required</span>}
              </div>
            </div>
            <div className="cp-auth-row cp-auth-row-textarea">
              <label className="cp-auth-label">Comments:</label>
              <div className="cp-auth-field">
                <textarea
                  className="cp-sv-textarea"
                  value={authComment}
                  onChange={(e) => setAuthComment(e.target.value)}
                  placeholder="Optional comments..."
                />
              </div>
            </div>
            {error && <div className="cp-auth-error">{error}</div>}
          </div>
          <div className="cp-modal-footer">
            <button className="oc-btn" onClick={() => {
              if (!reason) { setError('Please select a reason.'); return; }
              setStep(3);
            }}>Submit</button>
            <button className="oc-btn oc-btn-secondary" onClick={() => { setStep(1); setError(null); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-overlay">
      <div className="cp-modal cp-msg-modal">
        <div className="cp-modal-header">
          <span>Message</span>
          <button className="cp-modal-close-btn" onClick={onClose}>CLOSE ✕</button>
        </div>
        <div className="cp-modal-body cp-msg-body">
          <span className="cp-msg-text" style={{ color: 'var(--oc-error, #c0392b)' }}>
            This functionality is not supported at this point.
          </span>
        </div>
        <div className="cp-modal-footer">
          <button className="oc-btn" onClick={onClose}>Ok</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DETAIL FORM FIELDS (shared between add / edit / reply modes)
// ─────────────────────────────────────────────────────────────
function DetailFields({ form, updForm, onPropertyChange, isPopup, disabled, propertyList }) {
  return (
    <>
      {/* Row 1 */}
      <div className="cp-details-row1">
        <Field label="Priority" required={!disabled}>
          <select
            className="oc-select"
            value={form.priority}
            onChange={updForm("priority")}
            disabled={disabled}
          >
            <option value="">Select</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field label="Limit to Department">
          <select
            className="oc-select"
            value={form.limitDepartment}
            onChange={updForm("limitDepartment")}
            disabled={disabled}
          >
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </Field>

        {/* Multi-select; disabled when Global (All Properties) is checked */}
        <Field label="Limit to Property" className="cp-span-full">
          <PropertyMultiSelect
            value={form.limitProperty}
            onChange={onPropertyChange}
            disabled={disabled || form.globalAllProperties}
            placeholder={form.globalAllProperties ? "ALL" : "Select..."}
            propertyList={propertyList}
          />
        </Field>

        <label
          className={disabled ? "oc-check-label disabled" : "oc-check-label"}
          style={{ alignSelf: "flex-end", paddingBottom: 6 }}
        >
          <input
            type="checkbox"
            checked={form.globalAllProperties}
            onChange={updForm("globalAllProperties")}
            disabled={disabled}
          />
          <span>Global (All Properties)</span>
        </label>
      </div>

      {/* Row 2 */}
      <div className="cp-details-row2">
        <Field label="Comment Type" required={!disabled}>
          <select
            className="oc-select"
            value={form.commentType}
            onChange={updForm("commentType")}
            disabled={disabled}
          >
            <option value="">Select</option>
            {COMMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>

        <DateField
          label="Expiration Date"
          value={form.expirationDate}
          onChange={updForm("expirationDate")}
          disabled={disabled || form.never}
        />

        <label
          className={disabled ? "oc-check-label disabled" : "oc-check-label"}
          style={{ alignSelf: "flex-end", paddingBottom: 6 }}
        >
          <input
            type="checkbox"
            checked={form.never}
            onChange={updForm("never")}
            disabled={disabled}
          />
          <span>Never</span>
        </label>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function CommentsPage() {
  const isPopup          = usePopupMode();
  const modeCls          = isPopup ? "cp-popup" : "cp-full";
  const { toasts, show: showToast, dismiss: dismissToast } = useToast();

  // ── Auth user (for createdBy in POST/PUT payloads)
  // Same pattern as PointAdjustmentsPage: given_name.family_name from OIDC profile.
  // The id_token contains given_name, family_name, email from WSO2.
  const auth        = useAuthSafe();
  const givenName   = auth.user?.profile?.given_name  ?? '';
  const familyName  = auth.user?.profile?.family_name ?? '';
  const currentUser = givenName && familyName ? `${givenName}.${familyName}` : undefined;

  // ── URL / context state (read from URL params on mount)
  const [memberId,       setMemberId]       = useState('');
  const [pmsHotelId,     setPmsHotelId]     = useState('');
  // locationCode resolved from pmsHotelId via /v1/location/:pmsHotelId
  // Used as the default sourceProperty in POST/PUT payloads — not shown in UI.
  const [pmsPropertyCode, setPmsPropertyCode] = useState('');
  const [pmsError,        setPmsError]        = useState(false); // true when pmsHotelId missing from URL

  // ── Property list (fetched from /v1/location on mount)
  const [propertyList,        setPropertyList]        = useState([]);
  const [propertyListLoading, setPropertyListLoading] = useState(false);

  // Fetch locations once on component mount
  useEffect(() => {
    let cancelled = false;
    async function fetchPropertyList() {
      setPropertyListLoading(true);
      try {
        const data = await getLocations();
        if (!cancelled && Array.isArray(data)) setPropertyList(data);
      } catch (err) {
        console.error('[CommentsPage] Failed to load property list:', err);
      } finally {
        if (!cancelled) setPropertyListLoading(false);
      }
    }
    fetchPropertyList();
    return () => { cancelled = true; };
  }, []);

  // ── API loading / error state
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError,   setFetchError]   = useState(null);
  const [saveLoading,  setSaveLoading]  = useState(false);
  const [saveError,    setSaveError]    = useState(null);
  const [successMsg,   setSuccessMsg]   = useState(null); // drives the success popup

  // ── Filter state
  const [filters, setFilters] = useState({
    source: "All", department: "All", property: "All",
    searchFrom: "", searchTo: "", priority: "All",
  });

  // ── All rows fetched from API (never filtered by commentStatus on the server)
  const [allRows, setAllRows] = useState([]);
  // ── View mode: 'active' shows ACTIVE only; 'settledvoided' shows SETTLED + VOIDED
  const [viewMode, setViewMode] = useState('active');

  // ── Derived rows — filtered client-side based on viewMode
  const rows = useMemo(() => {
    if (viewMode === 'settledvoided') {
      return allRows.filter((r) => r.commentStatus === 'SETTLED' || r.commentStatus === 'VOIDED' || r.commentStatus === 'VOID');
    }
    return allRows.filter((r) => r.commentStatus === 'ACTIVE');
  }, [allRows, viewMode]);

  // ── Panel state
  const [mode,       setMode]       = useState(MODE.IDLE);
  const [selectedId, setSelectedId] = useState(null);
  const [form,       setForm]       = useState({ ...EMPTY_FORM });
  const [settleOpen, setSettleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ── Reply inline expansion state
  const [allReplies,   setAllReplies]   = useState([]);
  const [expandedIds,  setExpandedIds]  = useState(new Set());
  const [selectedReply, setSelectedReply] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId]
  );

  // Replies grouped by parent commentID string
  const repliesByCommentId = useMemo(() => {
    const map = {};
    for (const rep of allReplies) {
      const key = String(rep.parentCommentId);
      if (!map[key]) map[key] = [];
      map[key].push(rep);
    }
    return map;
  }, [allReplies]);

  // ── Date helpers ──────────────────────────────────────────────────────────
  // Convert ISO date/datetime → MM/DD/YYYY
  function formatDisplayDate(iso) {
    if (!iso || iso === 'null' || iso === 'undefined' || iso === 'none') return 'N/A';
    try {
      const datePart = iso.split('T')[0];
      const [y, m, d] = datePart.split('-');
      return y && m && d ? `${m}/${d}/${y}` : 'N/A';
    } catch { return 'N/A'; }
  }

  // Convert ISO datetime → "MM/DD/YYYY at HH:MM AM/PM"
  function formatAddedOn(iso) {
    if (!iso || iso === 'null' || iso === 'undefined' || iso === 'none') return '';
    try {
      const d   = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const date = d.toLocaleDateString('en-US');
      const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      return `${date} at ${time}`;
    } catch { return ''; }
  }

  // ── Map API comment object → table row shape ──────────────────────────────
  // API fields: commentId, comment, priority, commentSource, createdBy,
  //             department, limitToProperty, commentType, expireDate,
  //             sourceProperty, createdDate
  function apiCommentToRow(c, idx) {
    // GET response: "property" holds the limted-to property codes ("CC033,CC034") or "ALL"
    const isGlobal = !c.property || c.property === 'ALL';
    const propCodes = isGlobal
      ? []
      : c.property.split(',').map((s) => s.trim()).filter(Boolean);
    // API returns 'userName' as the display username field.
    // 'createdBy' is the raw value used in POST/PUT; 'userName' is what is shown in the UI.
    const displayUser = c.userName ?? c.createdBy ?? '';
    return {
      // API field is "commentID" (capital ID) — mandatory, always present in GET response.
      apiId:               c.commentID,
      // UI row key — same value; safe string conversion so React key is never a number.
      id:                  String(c.commentID),
      commentStatus:       c.commentStatus ?? 'ACTIVE',
      // API returns displayDate (YYYY-MM-DD) and displayDateTime (ISO)
      displayDate:         formatDisplayDate(c.displayDate),
      comment:             c.comment             ?? '',
      priority:            c.priority            ?? '',
      source:              c.commentSource        ?? '',
      userName:            displayUser,
      department:          c.department           ?? 'ALL',
      // Display field: raw property value from API (e.g. "CC033,CC034" or "ALL")
      property:            c.property             ?? 'ALL',
      commentType:         c.commentType          ?? '',
      // expireDate may be a full ISO datetime — only keep the date part.
      // Treat string 'null' / 'undefined' from the API the same as a missing value → empty string.
      expirationDate:      (c.expireDate && c.expireDate !== 'null' && c.expireDate !== 'undefined' && c.expireDate !== 'none')
                             ? c.expireDate.split('T')[0]
                             : '',
      globalAllProperties: isGlobal,
      // Array of individual property codes — populated into the multi-select on edit
      limitProperty:       propCodes,
      // sourceProperty = first code in the list (used in PUT payload), fallback to PROPERTY_CODE
      sourceProperty:      propCodes[0] ?? PROPERTY_CODE,
      limitDepartment:     c.department           ?? 'All',
      // Added By — uses the same userName field (what the UI shows)
      addedBy:             displayUser,
      addedOn:             formatAddedOn(c.displayDateTime),
      // Last Edited By — populated only when the record has been modified after creation
      modifiedBy:          c.modifiedBy ?? c.lastModifiedBy ?? '',
      modifiedOn:          formatAddedOn(c.modifiedDateTime ?? c.lastModifiedDate ?? ''),
    };
  }

  // ── Map form fields → POST / PUT request body ─────────────────────────────
  function formToPayload(f, { createdBy }) {
    const body = {
      comment:       f.commentText,
      priority:      f.priority      || '',
      commentType:   f.commentType   || '',
      commentSource: 'Opera',   // always hardcoded
    };
    // department is optional — omit entirely when "All" / "ALL" is selected
    const dept = f.limitDepartment;
    if (dept && dept !== 'All' && dept !== 'ALL') {
      body.department = dept;
    }
    // limitToProperty + sourceProperty logic:
    //   global (All Properties checked) → omit limitToProperty
    //   specific codes selected         → send limitToProperty
    //   sourceProperty is ALWAYS the locationCode resolved from the URL pmsHotelId
    if (f.globalAllProperties) {
      // ALL selected — limitToProperty is omitted
    } else if (f.limitProperty?.length) {
      // limitProperty already stores locationCode strings — send as-is
      const codes = f.limitProperty.map((c) => c.trim());
      body.limitToProperty = codes.join(',');
    }
    // sourceProperty always comes from the URL-resolved pmsHotelId locationCode
    body.sourceProperty = pmsPropertyCode || PROPERTY_CODE;
    if (!f.never && f.expirationDate)   body.expireDate = f.expirationDate;
    // createdBy is required by the API — always include it
    body.createdBy = createdBy || '';
    return body;
  }

  // ── Fetch comments from API ───────────────────────────────────────────────
  // Map API reply object → internal reply row
  function apiReplyToRow(r) {
    return {
      id:              `reply-${r.commentEntryID}`,
      commentEntryID: r.commentEntryID,
      // parentCommentId links back to the parent comment row id (string)
      parentCommentId: String(r.commentID),
      displayDate:     formatDisplayDate(r.displayDate),
      comment:         r.comment  ?? '',
      userName:        r.userName ?? '',
      addedOn:         formatAddedOn(r.displayDateTime),
      isEdited:        r.isEdited ?? false,
    };
  }

  const fetchComments = useCallback(async (mid, params) => {
    if (!mid) return;
    setFetchLoading(true);
    setFetchError(null);
    try {
      const data = await getMemberComments(mid, params);
      // API may return an array directly or { comments: [...], replies: [...] }
      const list = Array.isArray(data) ? data : (data?.comments ?? []);
      const replyList = Array.isArray(data) ? [] : (data?.replies ?? []);
      setAllRows(list.map((c, idx) => apiCommentToRow(c, idx)));
      setAllReplies(replyList.map((r) => apiReplyToRow(r)));
    } catch (err) {
      console.error('[CommentsPage] fetchComments failed:', err);
      setFetchError(err.message || 'Failed to load comments');
      setAllRows([]);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  // ── Read URL params on mount, then auto-fetch ─────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mid = urlParams.get('memberId') || urlParams.get('accountNumber');
    const pms = urlParams.get('pmsHotelId');
    if (!pms) {
      setPmsError(true);
      return;
    }
    if (mid) {
      console.log('[CommentsPage] memberId from URL:', mid);
      setMemberId(mid);
      setPmsHotelId(pms);
      // Resolve pmsHotelId → locationCode to use as sourceProperty in all payloads
      getLocation(pms)
        .then((res) => {
          const code = res?.locationCode;
          if (code) {
            console.log('[CommentsPage] resolved pmsPropertyCode:', code);
            setPmsPropertyCode(code);
          } else {
            setPmsError(true);
          }
        })
        .catch((err) => {
          console.warn('[CommentsPage] getLocation failed:', err);
          setPmsError(true);
        });
    }
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchComments(memberId, {});
    }
  }, [memberId, fetchComments]);

  // Scroll ref
  const detailsRef = useRef(null);
  function scrollToDetails() {
    requestAnimationFrame(() => {
      detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  // ── Generic updaters
  function upd(key)     { return (e) => setFilters((p) => ({ ...p, [key]: e.target.value })); }
  function updForm(key) {
    return (e) => {
      const newVal = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((p) => ({
        ...p,
        [key]: newVal,
        // Checking "Global (All Properties)" clears any multi-select choices
        ...(key === "globalAllProperties" && newVal === true ? { limitProperty: [] } : {}),
      }));
    };
  }

  // ── Helpers to populate form from a row
  function rowToForm(row) {
    return {
      priority:            row.priority            ?? "",
      limitDepartment:     row.limitDepartment      ?? "All",
      limitProperty:       Array.isArray(row.limitProperty) ? row.limitProperty : [],
      globalAllProperties: !!row.globalAllProperties,
      sourceProperty:      row.sourceProperty      ?? PROPERTY_CODE,
      commentType:         row.commentType          ?? "",
      expirationDate:      row.expirationDate        ?? "",
      never:               false,
      commentText:         row.comment              ?? "",
    };
  }

  // ── Actions
  function onSearch() {
    const params = {};
    if (filters.source     !== 'All') params.commentSource = filters.source;
    if (filters.priority   !== 'All') params.priority      = filters.priority;
    if (filters.property   !== 'All') params.propcode      = filters.property;
    if (filters.department !== 'All') params.department    = filters.department;
    if (filters.searchFrom)           params.fromDate      = filters.searchFrom;
    if (filters.searchTo)             params.toDate        = filters.searchTo;
    fetchComments(memberId, params);
  }

  function onRowClick(row) {
    setSelectedId(row.id);
    setForm(rowToForm(row));
    setMode(MODE.VIEW);
    setSelectedReply(null);
    setSaveError(null);
    // No auto-scroll — the details panel is always visible below the table.
  }

  function onAdd() {
    setSelectedId(null);
    setSelectedReply(null);
    setSaveError(null);
    setForm({ ...EMPTY_FORM });
    setMode(MODE.ADD);
    scrollToDetails();
  }

  function onEdit() {
    if (!selectedRow) return;
    setForm(rowToForm(selectedRow));
    setMode(MODE.EDIT);
    scrollToDetails();
  }

  function onReply() {
    if (!selectedRow) return;
    // Pre-fill form defaults; commentText is blank (user types their reply)
    setForm({ ...EMPTY_FORM, commentText: "" });
    setMode(MODE.REPLY);
    scrollToDetails();
  }

  function onDelete() {
    if (!selectedRow) return;
    setDeleteOpen(true);
  }

  function onCancel() {
    setSaveError(null);
    if (mode === MODE.EDIT_REPLY) {
      // Restore comment text to the original reply and go back to view
      setForm((p) => ({ ...p, commentText: selectedReply?.comment ?? '' }));
      setMode(MODE.VIEW_REPLY);
    } else if (mode === MODE.EDIT || mode === MODE.REPLY) {
      // Go back to view mode for the same row
      setMode(MODE.VIEW);
    } else {
      // add → back to idle
      setMode(MODE.IDLE);
      setSelectedId(null);
      setSelectedReply(null);
    }
    if (mode !== MODE.EDIT_REPLY) setForm({ ...EMPTY_FORM });
  }

  async function onSave() {
    // Reply and edit-reply actions are not yet supported — show inline message
    if (mode === MODE.REPLY || mode === MODE.EDIT_REPLY) {
      setSaveError('This functionality is not supported at this point.');
      return;
    }
    if (!memberId) {
      showToast('Error: no member ID — cannot save comment.');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      const payload = formToPayload(form, {
        createdBy: currentUser,
      });

      if (mode === MODE.ADD || mode === MODE.REPLY) {
        await postMemberComment(memberId, payload);
        const msg = mode === MODE.REPLY ? 'Reply added successfully.' : 'Comment created successfully.';
        // Refresh list in background; user dismisses popup to return to idle
        await fetchComments(memberId, {});
        setSuccessMsg(msg);
      } else if (mode === MODE.EDIT) {
        const targetId = selectedRow?.apiId;
        if (!targetId) {
          const msg = 'Cannot update: missing comment id for selected comment.';
          setSaveError(msg);
          throw new Error(msg);
        }
        await putMemberComment(memberId, targetId, payload);
        // Refresh list in background; user dismisses popup to return to view
        await fetchComments(memberId, {});
        setSuccessMsg('Comment updated successfully.');
      }
    } catch (err) {
      console.error('[CommentsPage] onSave failed:', err);
      const errTitle  = err.response?.title  || err.message || 'Failed to save comment';
      const errDetail  = err.response?.detail;
      setSaveError(errDetail ? `${errTitle} - ${errDetail}` : errTitle);
    } finally {
      setSaveLoading(false);
    }
  }

  async function onSettleVoidDone() {
    setSettleOpen(false);
    setMode(MODE.IDLE);
    setSelectedId(null);
    setSelectedReply(null);
    // Refresh full list; client-side filter moves the comment to the correct view
    await fetchComments(memberId, {});
  }

  // ── Reply row click: select the reply and show its parent context in the detail panel
  function onReplyRowClick(reply) {
    const parentRow = rows.find((r) => r.id === reply.parentCommentId);
    if (parentRow) {
      setForm(rowToForm(parentRow));
      setSelectedId(parentRow.id);
    }
    setSelectedReply(reply);
    setSaveError(null);
    setMode(MODE.VIEW_REPLY);
    scrollToDetails();
  }

  // ── Detail panel header label
  function detailsTitle() {
    switch (mode) {
      case MODE.VIEW:        return "Comment Details";
      case MODE.ADD:         return "Add Comment Details";
      case MODE.EDIT:        return "Edit Comment Details";
      case MODE.REPLY:       return "Reply to Comment";
      case MODE.VIEW_REPLY:  return "Comment Details";
      case MODE.EDIT_REPLY:  return "Edit Comment Details";
      default:               return "Comment Details";
    }
  }

  // ── Is panel dimmed?
  const isPanelDimmed = mode === MODE.IDLE;
  // ── Is form editable?
  const isEditing = mode === MODE.ADD || mode === MODE.EDIT || mode === MODE.REPLY;

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────

  // Guard: pmsHotelId is mandatory — without it we cannot resolve sourceProperty
  if (pmsError) {
    return (
      <div className={`cp-root ${modeCls}`}>
        <div className="cp-card" style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ color: 'var(--oc-error, #c0392b)', fontWeight: 600, fontSize: 14 }}>
            No Data available for this pmsHotelId.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`cp-root ${modeCls}`}>
        <div className="cp-card">

          {/* Title bar removed */}

          {/* ════════════════════════
              FILTER SECTION
          ════════════════════════ */}
          <div className="cp-filters" style={{ paddingTop: 12 }}>

            {/* Row 1 — Source | Department | Property */}
            <div className="cp-filter-row cp-filter-row1">
              <Field label="Source">
                <select className="oc-select" value={filters.source} onChange={upd("source")}>
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Department">
                <select className="oc-select" value={filters.department} onChange={upd("department")}>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </Field>
              {!isPopup && (
                <Field label="Property">
                  <select className="oc-select" value={filters.property} onChange={upd("property")}>
                    <option value="All">{propertyListLoading ? 'Loading...' : 'All'}</option>
                    {propertyList.map((p) => (
                      <option key={p.locationCode} value={p.locationCode}>{p.locationLegalName}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            {/* Row 2 — Search From | To | Priority | Property (popup) | Search btn (full) */}
            <div className="cp-filter-row cp-filter-row2">
              <DateField label="Search From" value={filters.searchFrom} onChange={upd("searchFrom")} />
              <DateField label="Search To"   value={filters.searchTo}   onChange={upd("searchTo")} />
              <Field label="Priority">
                <select className="oc-select" value={filters.priority} onChange={upd("priority")}>
                  <option value="All">All</option>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              {/* Property shown next to Priority in popup; shown in Row 1 in full mode */}
              {isPopup && (
                <Field label="Property">
                  <select className="oc-select" value={filters.property} onChange={upd("property")}>
                    <option value="All">{propertyListLoading ? 'Loading...' : 'All'}</option>
                    {propertyList.map((p) => (
                      <option key={p.locationCode} value={p.locationCode}>{p.locationLegalName}</option>
                    ))}
                  </select>
                </Field>
              )}
              {!isPopup && (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button className="oc-btn" onClick={onSearch}>Search</button>
                </div>
              )}
            </div>

            <div className="cp-filter-divider" />

            {/* Action bar */}
            <div className="cp-filter-actions">
              {isPopup && (
                <button className="oc-btn cp-search-btn" onClick={onSearch}>Search</button>
              )}
              {viewMode === 'settledvoided' ? (
                // In settled/voided view → only "View All" is shown
                <button
                  className="oc-btn oc-btn-secondary"
                  onClick={() => { setViewMode('active'); setMode(MODE.IDLE); setSelectedId(null); setSelectedReply(null); }}
                >
                  View All
                </button>
              ) : (
                // Normal active view → Add + View Settled/Voided
                <>
                  <button className="oc-btn oc-btn-secondary" onClick={onAdd}>Add</button>
                  <button
                    className="oc-btn oc-btn-secondary"
                    onClick={() => { setViewMode('settledvoided'); setMode(MODE.IDLE); setSelectedId(null); setSelectedReply(null); }}
                  >
                    {isPopup ? "Settled/Voided" : "View Settled/Voided"}
                  </button>
                </>
              )}
            </div>

          </div>

          {/* ════════════════════════
              RESULTS TABLE
              All 7 columns shown.
              Comment cell truncates with ellipsis.
              Horizontal scroll if too narrow.
          ════════════════════════ */}
          <div className="cp-table-wrap oc-table-wrap">
            <table className="oc-table cp-table">
              <thead>
                <tr>
                  <th>Display Date</th>
                  <th>Comment</th>
                  <th>Priority</th>
                  <th>Source</th>
                  <th>UserName</th>
                  <th>Department</th>
                  <th>Property</th>
                </tr>
              </thead>
              <tbody>
                {fetchLoading ? (
                  <tr className="oc-empty-row">
                    <td colSpan={7}>Loading comments...</td>
                  </tr>
                ) : fetchError ? (
                  <tr className="oc-empty-row">
                    <td colSpan={7} style={{ color: 'var(--oc-error, #c0392b)' }}>Error: {fetchError}</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className="oc-empty-row">
                    <td colSpan={7}>No comments found. Enter or modify criteria and search.</td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const isSelected  = r.id === selectedId && mode !== MODE.VIEW_REPLY && mode !== MODE.EDIT_REPLY;
                    const rowBg       = isSelected ? '#C8E8EB' : '#ffffff';
                    const hasReplies  = !!(repliesByCommentId[r.id]?.length);
                    const isExpanded  = expandedIds.has(r.id);
                    function toggleExpand(e) {
                      e.stopPropagation();
                      setExpandedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                        return next;
                      });
                    }
                    return (
                      <React.Fragment key={r.id}>
                        <tr
                          className={isSelected ? "oc-selected" : ""}
                          onClick={() => onRowClick(r)}
                          title={r.comment}
                        >
                          <td style={{ background: rowBg }} className="cp-date-cell">
                            <div className="cp-date-inner">
                              {hasReplies ? (
                                <button
                                  className="cp-expand-btn"
                                  onClick={toggleExpand}
                                  title={isExpanded ? 'Collapse replies' : 'Expand replies'}
                                >{isExpanded ? '\u25bc' : '\u25ba'}</button>
                              ) : (
                                <span className="cp-expand-spacer" />
                              )}
                              <span>{r.displayDate}</span>
                            </div>
                          </td>
                          <td style={{ background: rowBg }} className="col-comment">{r.comment}</td>
                          <td style={{ background: rowBg }}>{r.priority}</td>
                          <td style={{ background: rowBg }}>{r.source}</td>
                          <td style={{ background: rowBg }}>{r.userName}</td>
                          <td style={{ background: rowBg }}>{r.department}</td>
                          <td style={{ background: rowBg }} className="col-property" title={r.property}>{r.property}</td>
                        </tr>
                        {isExpanded && hasReplies && (
                          <tr className="cp-reply-rows-tr">
                            <td colSpan={7} className="cp-reply-rows-td">
                              <table className="cp-reply-subtable">
                                <colgroup>
                                  <col style={{ width: '100px' }} />
                                  <col />
                                  <col style={{ width: '140px' }} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th>Display Date</th>
                                    <th>Comment</th>
                                    <th>UserName</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {repliesByCommentId[r.id].map((rep) => {
                                    const isRepSel = selectedReply?.id === rep.id;
                                    return (
                                      <tr
                                        key={rep.id}
                                        className={isRepSel ? 'oc-selected' : ''}
                                        onClick={(e) => { e.stopPropagation(); onReplyRowClick(rep); }}
                                      >
                                        <td>{rep.displayDate}</td>
                                        <td className="col-comment">{rep.comment}</td>
                                        <td>{rep.userName}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ════════════════════════
              DETAILS PANEL
          ════════════════════════ */}
          <div
            ref={detailsRef}
            className={isPanelDimmed ? "cp-details cp-dimmed" : "cp-details"}
          >

            {/* Section header */}
            <div className="cp-details-header">{detailsTitle()}</div>

            <div className="cp-details-body">

              {/* ──────────────────────────────
                  VIEW MODE
                  Read-only fields + Reply/Edit/Delete/Settle buttons
              ────────────────────────────── */}
              {mode === MODE.VIEW && selectedRow && (
                <>
                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={true}
                    propertyList={propertyList}
                  />

                  {/* Added by / Last Edited by */}
                  <div className="cp-view-meta">
                    <div>
                      Added By: <strong>{selectedRow.addedBy || 'N/A'}</strong>
                      {selectedRow.addedOn ? ` on ${selectedRow.addedOn}` : ''}
                    </div>
                    {selectedRow.modifiedBy ? (
                      <div>
                        Last Edited By: <strong>{selectedRow.modifiedBy}</strong>
                        {selectedRow.modifiedOn ? ` on ${selectedRow.modifiedOn}` : ''}
                      </div>
                    ) : null}
                  </div>
                  <div className="cp-view-text">{selectedRow.comment}</div>

                  {/* Action buttons */}
                  <div className="cp-footer-view">
                    {/* TODO: Uncomment when backend endpoints are ready
                    <button className="oc-btn oc-btn-secondary" onClick={onReply}>Reply</button>
                    */}
                    <button className="oc-btn oc-btn-secondary" onClick={onEdit}>Edit</button>
                    {/* TODO: Uncomment when backend endpoints are ready
                    <button className="oc-btn oc-btn-secondary" onClick={onDelete}>Delete</button>
                    <button className="oc-btn oc-btn-secondary" onClick={() => setSettleOpen(true)}>
                      Settle/Void
                    </button>
                    */}
                  </div>
                </>
              )}

              {/* ──────────────────────────────
                  EDIT MODE
              ────────────────────────────── */}
              {mode === MODE.EDIT && (
                <>
                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={false}
                    propertyList={propertyList}
                  />

                  <div className="cp-comment-box">
                    <label className="oc-label required" style={{ display: 'block', marginBottom: 4 }}>Comment</label>
                    <div className="cp-added-by">
                      <span>
                        Added By: <strong>{selectedRow?.addedBy || 'N/A'}</strong>
                        {selectedRow?.addedOn ? ` on ${selectedRow.addedOn}` : ''}
                      </span>
                      {selectedRow?.modifiedBy ? (
                        <span style={{ marginLeft: 16 }}>
                          Last Edited By: <strong>{selectedRow.modifiedBy}</strong>
                          {selectedRow?.modifiedOn ? ` on ${selectedRow.modifiedOn}` : ''}
                        </span>
                      ) : null}
                    </div>
                    <textarea
                      className="cp-textarea"
                      value={form.commentText}
                      onChange={updForm("commentText")}
                      placeholder="Enter comment..."
                    />
                  </div>

                  <div className="cp-footer-edit">
                    {saveError && <span className="cp-save-error">{saveError}</span>}
                    <button className="oc-btn oc-btn-secondary" onClick={onCancel} disabled={saveLoading}>Cancel</button>
                    <button className="oc-btn" onClick={onSave} disabled={saveLoading}>{saveLoading ? 'Saving...' : 'Save'}</button>
                  </div>
                </>
              )}

              {/* ──────────────────────────────
                  ADD MODE
              ────────────────────────────── */}
              {mode === MODE.ADD && (
                <>
                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={false}
                    propertyList={propertyList}
                  />

                  <div className="cp-comment-box">
                    <label className="oc-label required" style={{ display: 'block', marginBottom: 4 }}>Comment</label>
                    <textarea
                      className="cp-textarea"
                      value={form.commentText}
                      onChange={updForm("commentText")}
                      placeholder="Enter comment..."
                    />
                  </div>

                  <div className="cp-footer-edit">
                    {saveError && <span className="cp-save-error">{saveError}</span>}
                    <button className="oc-btn oc-btn-secondary" onClick={onCancel} disabled={saveLoading}>Cancel</button>
                    <button className="oc-btn" onClick={onSave} disabled={saveLoading}>{saveLoading ? 'Saving...' : 'Save'}</button>
                  </div>
                </>
              )}

              {/* ──────────────────────────────
                  REPLY MODE
              ────────────────────────────── */}
              {mode === MODE.REPLY && selectedRow && (
                <>
                  {/* Show what we're replying to */}
                  <div className="cp-reply-badge">
                    <span className="cp-reply-badge-label">Replying to:</span>
                    <span className="cp-reply-badge-text">{selectedRow.comment}</span>
                  </div>

                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={true}
                    propertyList={propertyList}
                  />

                  <div className="cp-comment-box">
                    <label className="oc-label required" style={{ display: 'block', marginBottom: 4 }}>Comment</label>
                    <textarea
                      className="cp-textarea"
                      value={form.commentText}
                      onChange={updForm("commentText")}
                      placeholder="Enter your reply..."
                    />
                  </div>

                  <div className="cp-footer-edit">
                    {saveError && <span className="cp-save-error">{saveError}</span>}
                    <button className="oc-btn oc-btn-secondary" onClick={onCancel} disabled={saveLoading}>Cancel</button>
                    <button className="oc-btn" onClick={onSave} disabled={saveLoading}>{saveLoading ? 'Saving...' : 'Save Reply'}</button>
                  </div>
                </>
              )}

              {/* ──────────────────────────────
                  VIEW REPLY MODE
                  Shows parent comment fields (read-only) + reply text
                  Buttons: Reply | Edit | Delete (all show "not supported" inline)
              ────────────────────────────── */}
              {mode === MODE.VIEW_REPLY && selectedReply && (
                <>
                  {/* Parent comment fields — always read-only */}
                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={true}
                    propertyList={propertyList}
                  />

                  {/* Replied By meta line */}
                  <div className="cp-view-meta">
                    <div>
                      Replied By: <strong>{selectedReply.userName}</strong>
                      {selectedReply.addedOn ? ` on ${selectedReply.addedOn}` : ''}
                    </div>
                  </div>

                  {/* Reply text (read-only) */}
                  <div className="cp-view-text">{selectedReply.comment}</div>

                  {/* TODO: Uncomment when backend endpoints are ready
                  <div className="cp-footer-view">
                    {saveError && (
                      <span className="cp-save-error" style={{ flex: '0 1 100%', textAlign: 'center', marginBottom: 4 }}>
                        {saveError}
                      </span>
                    )}
                    <button
                      className="oc-btn oc-btn-secondary"
                      onClick={() => setSaveError('This functionality is not supported at this point.')}
                    >Reply</button>
                    <button
                      className="oc-btn oc-btn-secondary"
                      onClick={() => {
                        setSaveError(null);
                        setForm((p) => ({ ...p, commentText: selectedReply.comment }));
                        setMode(MODE.EDIT_REPLY);
                        scrollToDetails();
                      }}
                    >Edit</button>
                    <button
                      className="oc-btn oc-btn-secondary"
                      onClick={() => setSaveError('This functionality is not supported at this point.')}
                    >Delete</button>
                  </div>
                  */}
                </>
              )}

              {/* ──────────────────────────────
                  EDIT REPLY MODE
                  Parent fields read-only; only reply text is editable.
                  Save → "not supported" error; Cancel → VIEW_REPLY
              ────────────────────────────── */}
              {mode === MODE.EDIT_REPLY && selectedReply && (
                <>
                  {/* Parent comment fields — always read-only */}
                  <DetailFields
                    form={form}
                    updForm={updForm}
                    onPropertyChange={(v) => setForm((p) => ({ ...p, limitProperty: v }))}
                    isPopup={isPopup}
                    disabled={true}
                    propertyList={propertyList}
                  />

                  <div className="cp-comment-box">
                    <label className="oc-label required" style={{ display: 'block', marginBottom: 4 }}>Comment</label>
                    <div className="cp-added-by">
                      <span>
                        Replied By: <strong>{selectedReply.userName}</strong>
                        {selectedReply.addedOn ? ` on ${selectedReply.addedOn}` : ''}
                      </span>
                    </div>
                    <textarea
                      className="cp-textarea"
                      value={form.commentText}
                      onChange={updForm("commentText")}
                      placeholder="Edit reply..."
                    />
                  </div>

                  <div className="cp-footer-edit">
                    {saveError && <span className="cp-save-error">{saveError}</span>}
                    <button className="oc-btn oc-btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="oc-btn" onClick={onSave}>Save</button>
                  </div>
                </>
              )}

            </div>
          </div>
          {/* end .cp-details */}

        </div>
        {/* end .cp-card */}
      </div>

      {/* Success popup for add / edit / reply */}
      {successMsg && (
        <SuccessMessageModal
          message={successMsg}
          onOk={() => {
            setSuccessMsg(null);
            setMode(MODE.IDLE);
            setSelectedId(null);
            setForm({ ...EMPTY_FORM });
          }}
        />
      )}

      {/* Delete flow (3-step: confirm → authorization → not-supported) */}
      {deleteOpen && (
        <DeleteFlow
          onClose={() => setDeleteOpen(false)}
        />
      )}

      {/* Settle/Void flow (3-step: select → authorization → success) */}
      {settleOpen && (
        <SettleVoidFlow
          selectedRow={selectedRow}
          memberId={memberId}
          currentUser={currentUser}
          onDone={onSettleVoidDone}
          onClose={() => setSettleOpen(false)}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} dismiss={dismissToast} />
    </>
  );
}